/**
 * EraPack theme: shared helpers.
 *
 * Loaded first (in the head, as a module), so every other behaviour file can
 * import from it or read it off `window.EraPack`. No framework, no DOM work
 * on load.
 */

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });

/** Pence (what the Cart AJAX API returns) to "£1,234.56". */
export function formatMoney(pence) {
  const n = Number(pence);
  return GBP.format((Number.isFinite(n) ? n : 0) / 100);
}

/**
 * Format a per-cup price in pounds: "£0.106", "£0.075".
 * Three decimals, exactly as lib/products.ts perCupLabel: a cup costs
 * single-digit pence, so two decimals would collapse neighbouring volume
 * bands into the same number.
 */
export function perCupLabel(perCup) {
  return `£${Number(perCup).toFixed(3)}`;
}

/** The same figure in pence, the way a cup price is said out loud: "8.6p". */
export function penceLabel(perCup) {
  return `${(Number(perCup) * 100).toFixed(1)}p`;
}

/**
 * fetch() for JSON endpoints (the Cart AJAX API and friends). Sends and
 * expects JSON; a non-2xx answer throws an Error carrying Shopify's
 * `description` / `message` and the status, so callers can show it.
 */
export async function fetchJson(url, options = {}) {
  const { body, headers, ...rest } = options;
  const init = {
    ...rest,
    headers: {
      Accept: "application/json",
      ...(body !== undefined && !(body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...(headers || {}),
    },
  };
  if (body !== undefined) {
    init.body = body instanceof FormData || typeof body === "string" ? body : JSON.stringify(body);
  }
  const res = await fetch(url, init);
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && (data.description || data.message)) ||
      `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * Keeps keyboard focus inside an open dialog and hands it back on release.
 *
 * A port of lib/useFocusTrap.ts: focus moves into `root` (to `initial` when
 * given, otherwise its first control) and Tab wraps at either end rather
 * than escaping to the page behind. The move is tried straight away and
 * again when the panel's opening transition ends, because focus() on an
 * element that is still `visibility: hidden` does nothing. The returned
 * function releases the trap and gives focus back to whatever had it.
 */
export function trapFocus(root, initial) {
  if (!root) return () => {};
  const before = document.activeElement;

  const enter = () => {
    if (root.contains(document.activeElement)) return;
    const target = initial || focusables(root)[0];
    if (target) target.focus({ preventScroll: true });
  };
  const raf = requestAnimationFrame(enter);
  const timer = window.setTimeout(enter, 60);
  root.addEventListener("transitionend", enter);

  const onKey = (e) => {
    if (e.key !== "Tab") return;
    const list = focusables(root);
    if (list.length === 0) return;
    const first = list[0];
    const last = list[list.length - 1];
    const current = document.activeElement;
    if (!root.contains(current)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && current === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  };
  document.addEventListener("keydown", onKey);

  return () => {
    cancelAnimationFrame(raf);
    window.clearTimeout(timer);
    root.removeEventListener("transitionend", enter);
    document.removeEventListener("keydown", onKey);
    if (before && typeof before.focus === "function") before.focus({ preventScroll: true });
  };
}

/**
 * Body scroll lock, counted so two open panels (the menu over the search,
 * the cart over either) release it only when the last one closes. Pass
 * `false` to unlock. `unlockScroll()` is the same as `lockScroll(false)`.
 */
let locks = 0;
export function lockScroll(on = true) {
  locks = Math.max(0, locks + (on ? 1 : -1));
  document.body.style.overflow = locks > 0 ? "hidden" : "";
  return locks;
}
export function unlockScroll() {
  return lockScroll(false);
}

/** Dispatch a namespaced DOM event on `document`; returns false if cancelled. */
export function emit(name, detail, { cancelable = false } = {}) {
  return document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, cancelable }));
}

const helpers = { formatMoney, perCupLabel, penceLabel, fetchJson, trapFocus, lockScroll, unlockScroll, emit };
window.EraPack = Object.assign(window.EraPack || {}, helpers);

export default helpers;
